<template>
  <main-layout :containerFluid="false">
    <template v-slot:header>
        <navigation-back :to="$route.params.returnTo"></navigation-back>

        <div class="d-flex justify-space-between mb-8">
            <h1
              :class="$vuetify.breakpoint.smAndDown ? 'text-h5' : 'text-h4'"
              class="font-weight-bold"
            >
              {{$t('Subscription details')}}
              <template v-if="subscription"> ({{ subscription.place.name }})</template>
            </h1>
            <div class="d-flex flex-column flex-sm-row align-end align-sm-center">
              <v-menu
                  v-model="showOptionsMenu"
                  v-if="subscription && (canEditPaymentMethod(subscription) || canCancel(subscription))"
                  bottom
                  left
                  offset-y
              >
                  <template v-slot:activator="{ on, attrs }">
                      <v-btn
                          v-bind="attrs"
                          v-on="on"
                          text
                          class="single-subscription-guide-options-activator"
                      >
                          <v-icon>mdi-dots-vertical</v-icon>
                          <span class="text-none text-decoration-underline">{{ $t('Options') }}</span>
                      </v-btn>
                  </template>
                  <v-list dense>
                      <v-list-item
                          v-if="canEditPaymentMethod(subscription)"
                          @click="openEditPaymentMethod"
                          class="single-subscription-guide-edit-payment-action"
                      >
                          <v-list-item-icon class="mr-2">
                              <v-icon small color="primary">mdi-credit-card-edit-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>
                              {{ $t('Edit payment method') }}
                          </v-list-item-title>
                      </v-list-item>

                      <v-list-item
                          v-if="canCancel(subscription)"
                          @click="$refs.cancelReservation.open(subscription)"
                      >
                          <v-list-item-icon class="mr-2">
                              <v-icon small color="error">mdi-cancel</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title class="error--text">
                              {{ $t('Cancel subscription') }}
                          </v-list-item-title>
                      </v-list-item>
                  </v-list>
              </v-menu>
              <guide-help-button guide-name="single-subscription" />
            </div>
        </div>
    </template>
    <template v-slot:body>
      <v-row v-if="subscription">
        <v-col cols="12" md="4">
          <v-card outlined class="rounded-lg single-subscription-guide-details-card">
            <v-card-text class="pa-6">
              <div class="mb-6 black--text">
                <h2 class="text-h6">{{ getPlanName(subscription) }}</h2>
              </div>

              <v-divider class="mb-6"></v-divider>

              <v-list dense flat class="pa-0">
                <!-- Status -->
                <v-list-item class="px-0 mb-2">
                  <div class="d-flex justify-space-between align-center w-full">
                    <subscription-status
                      :subscription="subscription"
                      :show-cancel-option="hasPendingCancellation(subscription)"
                    />
                  </div>
                </v-list-item>

                <!-- Business name -->
                <v-list-item class="px-0 mb-2">
                  <v-list-item-icon class="mr-3 my-auto">
                    <v-icon>mdi-domain</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="pa-0">
                    <div class="d-flex justify-space-between align-center overflow-hidden">
                      <span class="text-body-2 mr-2">{{ $t('Business') }}</span>
                      <div class="text-right">
                        <template v-if="canViewBusiness(subscription)">
                          <a
                            :href="getSingleBusinessUrl(subscription.place_id)"
                            target="_blank"
                          >
                            {{ subscription.place.name }}
                          </a>
                        </template>
                        <template v-else>
                          {{ subscription.place.name }}
                        </template>
                        <div v-if="subscription.place.location && subscription.place.location.shortFormattedAddress" class="caption">
                          {{ subscription.place.location.shortFormattedAddress }}
                        </div>
                      </div>
                    </div>
                  </v-list-item-content>
                </v-list-item>

                <!-- Started at -->
                <v-list-item class="px-0 mb-2">
                  <v-list-item-icon class="mr-3 my-auto">
                    <v-icon>mdi-calendar-start</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="pa-0">
                    <div class="d-flex justify-space-between align-center">
                      <span class="text-body-2">{{ $t('Started at') }}</span>
                      <span class="text-body-2 font-weight-medium">{{ fullDateFormat(subscription.data.started_at) }}</span>
                    </div>
                  </v-list-item-content>
                </v-list-item>

                <!-- Started at -->
                <v-list-item class="px-0 mb-2">
                  <v-list-item-icon class="mr-3 my-auto">
                    <v-icon>mdi-cash</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="pa-0">
                    <div class="d-flex justify-space-between align-center">
                      <span class="text-body-2">{{ $t('Price') }}</span>
                      <span class="text-body-2 font-weight-medium">{{ subscription.formatted_pricing.sum_total }}</span>
                    </div>
                  </v-list-item-content>
                </v-list-item>

                <!-- Billing Cycle -->
                <v-list-item class="px-0 mb-2">
                  <v-list-item-icon class="mr-3 my-auto">
                    <v-icon>mdi-autorenew</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="pa-0">
                    <div class="d-flex justify-space-between align-center">
                      <span class="text-body-2">{{ $t('Billing cycle') }}</span>
                      <span class="text-body-2 font-weight-medium text-capitalize">{{ $t(`billing_cycle_${subscription.data.billing_cycle.interval}`) }}</span>
                    </div>
                  </v-list-item-content>
                </v-list-item>

                <!-- Next billing -->
                <v-list-item v-if="subscription.data.current_billing_period" class="px-0 mb-2">
                  <v-list-item-icon class="mr-3 my-auto">
                    <v-icon>mdi-calendar-refresh</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="pa-0">
                    <div class="d-flex justify-space-between align-center">
                      <span class="text-body-2">{{ $t('Next billing') }}</span>
                      <span class="text-body-2 font-weight-medium">{{ fullDateFormat(subscription.data.current_billing_period.ends_at) }}</span>
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>

              <!-- Pending cancellation notice -->
              <v-alert
                v-if="hasPendingCancellation(subscription)"
                type="warning"
                text
                dense
                class="mt-6 mb-0 rounded-lg"
                icon="mdi-clock-alert-outline"
              >
                <div class="text-body-2" v-html="
                  $t('cancelSubscriptionConfirmModal.description', {
                      date: getSubscriptionEndsAt(subscription)
                          ? `(<b>${fullDateFormat(getSubscriptionEndsAt(subscription))}</b>)`
                          : ''
                  })
                "></div>
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="8">
          <v-card outlined class="single-subscription-guide-billing-card">
            <v-card-title class="font-weight-bold">
              {{ $t('Billing') }}
            </v-card-title>
            <v-divider></v-divider>
            <transactions-with-pagination
              v-if="transactions.length"
              :transactions="transactions"
              :pagination="pagination"
              :show-business="false"
              :show-subscription="false"
              @load="loadTransactions"
            />
            <div
                v-else-if="!loading"
                class="pa-3 d-flex align-center"
            >
                <v-icon>mdi-file-document-outline</v-icon>
                <p class="mb-0 ml-2 text-subtitle-1">
                    {{ $t('You do not have any transactions') }}.
                </p>
            </div>
          </v-card>
        </v-col>
      </v-row>
      <cancel-subscription
          ref="cancelReservation"
          @cancelled="loadTransactionsDelayed"
      />

      <Modal
        v-if="showEditPaymentMethodModal"
        :show="showEditPaymentMethodModal"
        :title="$t('Edit payment method')"
        :hide-actions="false"
        max-width="600"
        @close="showEditPaymentMethodModal = false"
      >
        <div class="pa-4">
          <div class="paddle-checkout-container"></div>
        </div>
      </Modal>
    </template>
  </main-layout>
</template>

<script>
import axios from 'axios';
import { fullDateFormat } from '@/components/calendar/config';
import { mapGetters } from 'vuex';
import TransactionsWithPagination from '@/components/subscriptions/TransactionsWithPagination.vue';
import CurrencyMixin from '@/lib/CurrencyMixin';
import SubscriptionStatus from '@/components/subscriptions/SubscriptionStatus.vue';
import SubscriptionsMixin from '@/lib/SubscriptionsMixin';
import CancelSubscription from '@/components/subscriptions/CancelSubscription.vue';
import Modal from '@/components/Modal.vue';
import NavigationBack from '@/components/NavigationBack.vue';
import GuideHelpButton from '@/components/GuideHelpButton.vue';

export default {
  components: {
    TransactionsWithPagination,
    SubscriptionStatus,
    CancelSubscription,
    Modal,
    NavigationBack,
    GuideHelpButton,
  },
  mixins: [CurrencyMixin, SubscriptionsMixin],
  props: {
    id: {
      required: true,
    },
  },
  data() {
    return {
      subscription: null,
      transactions: [],
      pagination: {},
      showEditPaymentMethodModal: false,
      showOptionsMenu: false,
    };
  },
  computed: {
    ...mapGetters(['loading']),
  },
  mounted() {
    this.$title = this.$t('Subscription details');
    window.addEventListener('onboarding:single-subscription-guide-open-options-menu', this.openGuideOptionsMenu);
    window.addEventListener('onboarding:single-subscription-guide-close-options-menu', this.closeGuideOptionsMenu);
    this.loadTransactions();
    this.loadPaddle();
  },
  beforeDestroy() {
    window.removeEventListener('onboarding:single-subscription-guide-open-options-menu', this.openGuideOptionsMenu);
    window.removeEventListener('onboarding:single-subscription-guide-close-options-menu', this.closeGuideOptionsMenu);
  },
  methods: {
    fullDateFormat,
    loadTransactions(url = `/transactions/${this.id}`) {
      this.$store.dispatch('loading', true);
      axios.get(url)
        .then(({ data }) => {
          this.pagination = data.data;
          this.transactions = data.data.data;
          this.subscription = data.subscription;

          this.$store.dispatch('loading', false);
          this.$nextTick(() => {
            this.$store.dispatch('startSingleSubscriptionGuideFlow', {
              routeName: this.$route.name,
            });
          });
        })
        .catch(() => {
          this.$store.dispatch('loading', false);
        });
    },
    openEditPaymentMethod() {
      this.$store.dispatch('loading', true);
      axios.get(`/subscriptions/${this.subscription.id}/update-payment-method-transaction`)
        .then(({ data }) => {
          this.$store.dispatch('loading', false);
          this.showEditPaymentMethodModal = true;
          this.$nextTick(() => {
            this.initPaddleEdit(data);
          });
        })
        .catch((error) => {
          this.$store.dispatch('loading', false);

          if (error.response && error.response.data && error.response.data.errors) {
            this.$store.dispatch('setErrors', [error.response.data.errors]);
          } else if (error.response && error.response.data && error.response.data.message) {
            this.$store.dispatch('setErrors', [[error.response.data.message]]);
          }
        });
    },
    loadPaddle() {
      if (window.Paddle) {
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
      document.head.appendChild(script);
    },
    openGuideOptionsMenu() {
      this.showOptionsMenu = true;
    },
    closeGuideOptionsMenu() {
      this.showOptionsMenu = false;
    },
    initPaddleEdit(data) {
      if (!window.Paddle) {
        return;
      }
      window.Paddle.Environment.set(data.environment);
      window.Paddle.Initialize({
        token: data.client_token,
        eventCallback: (event) => {
          if (event.name === 'checkout.completed') {
            this.showEditPaymentMethodModal = false;
            this.$store.dispatch('message', this.$t('Subscription payment method updated'));
            this.loadTransactionsDelayed();
          }
        },
        checkout: {
          settings: {
            displayMode: 'inline',
            frameTarget: 'paddle-checkout-container',
            frameStyle: 'width: 100%; min-height: 500px;',
            theme: 'light',
            variant: 'one-page',
          },
        },
      });
      // eslint-disable-next-line
      Paddle.Checkout.open({
        transactionId: data.transaction_id,
      });
    },
    loadTransactionsDelayed() {
      setTimeout(() => {
        this.loadTransactions();
      }, 1000);
    },
  },
};
</script>
